import contentInventory from "./content-inventory.json";
import { ARTICLE_IDENTITIES } from "./article-manifest";
import { requireLocale, type Locale } from "./locales";
import { parsePublicPathname } from "./paths";

export type PublicationStatus = "draft" | "reviewed" | "published";
export type ContentVersion = Readonly<{
  locale: Locale;
  publicPath: string;
  status: PublicationStatus;
  sourceRevision?: string;
}>;
export type ContentRecord = Readonly<{
  id: string;
  versions: readonly ContentVersion[];
}>;
export type CounterpartResult =
  | Readonly<{
      kind: "available";
      contentId: string;
      href: string;
      locale: Locale;
    }>
  | Readonly<{
      kind: "unavailable";
      contentId: string;
      locale: Locale;
      englishPath: string;
      reason:
        | "source-unpublished"
        | "translation-missing"
        | "translation-unpublished";
    }>
  | Readonly<{
      kind: "unregistered";
      sourcePath: string;
      locale: Locale;
    }>;

export type ContentRegistry = Readonly<{
  getRecords: () => readonly ContentRecord[];
  getContent: (id: string) => ContentRecord | undefined;
  getPublishedVersions: (id: string) => readonly ContentVersion[];
  resolveCounterpart: (
    sourcePath: unknown,
    targetLocale: unknown
  ) => CounterpartResult;
}>;
const EMPTY_VERSIONS: readonly ContentVersion[] = Object.freeze([]);
const CONTENT_ID = /^[a-z0-9]+(?:[.-][a-z0-9]+)*$/;

function fail(message: string): never {
  throw new TypeError(`Invalid content registry: ${message}`);
}
function isRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value))
    return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
function copyVersion(value: unknown): ContentVersion {
  if (!isRecord(value)) fail("version must be a plain record");
  const locale = requireLocale(value.locale);
  const parsed = parsePublicPathname(value.publicPath);
  if (parsed.locale !== locale) fail("path and locale disagree");
  const status = value.status;
  if (status !== "draft" && status !== "reviewed" && status !== "published") {
    fail("unknown publication status");
  }
  const revision = value.sourceRevision;
  if (
    revision !== undefined &&
    (typeof revision !== "string" || !revision || revision.trim() !== revision)
  ) {
    fail("source revision must be a nonempty trimmed string");
  }
  return Object.freeze({
    locale,
    publicPath: value.publicPath as string,
    status,
    ...(revision === undefined ? {} : { sourceRevision: revision as string }),
  });
}

/** Local editorial data only. Validation is not evidence that a page is deployed. */
export function createContentRegistry(
  input: readonly ContentRecord[]
): ContentRegistry {
  if (!Array.isArray(input)) fail("records must be an array");
  const byId = new Map<string, ContentRecord>();
  const canonicalPaths = new Set<string>();
  const byPath = new Map<
    string,
    { record: ContentRecord; version: ContentVersion }
  >();
  const published = new Map<string, readonly ContentVersion[]>();
  for (const candidate of input) {
    if (
      !isRecord(candidate) ||
      typeof candidate.id !== "string" ||
      !CONTENT_ID.test(candidate.id)
    )
      fail("invalid content identity");
    if (byId.has(candidate.id)) fail("duplicate content identity");
    if (!Array.isArray(candidate.versions)) fail("versions must be an array");
    const versions = candidate.versions.map(copyVersion);
    if (new Set(versions.map((v) => v.locale)).size !== versions.length) {
      fail("duplicate locale within a content identity");
    }
    const english = versions.find((v) => v.locale === "en");
    if (english?.status !== "published")
      fail("a published English original is required");
    const record: ContentRecord = Object.freeze({
      id: candidate.id,
      versions: Object.freeze(versions),
    });
    for (const version of versions) {
      const decodedPath = decodeURIComponent(version.publicPath);
      if (canonicalPaths.has(decodedPath)) fail("duplicate public path");
      canonicalPaths.add(decodedPath);
      byPath.set(version.publicPath, { record, version });
    }
    byId.set(record.id, record);
    published.set(
      record.id,
      Object.freeze(versions.filter((v) => v.status === "published"))
    );
  }
  function resolveCounterpart(
    sourcePath: unknown,
    targetLocale: unknown
  ): CounterpartResult {
    const locale = requireLocale(targetLocale);
    parsePublicPathname(sourcePath);
    const source = byPath.get(sourcePath as string);
    if (!source) {
      return Object.freeze({
        kind: "unregistered",
        sourcePath: sourcePath as string,
        locale,
      });
    }
    const { record, version } = source;
    const target = record.versions.find((v) => v.locale === locale);
    const englishPath = record.versions.find(
      (v) => v.locale === "en"
    )!.publicPath;
    const reason =
      version.status !== "published"
        ? "source-unpublished"
        : !target
        ? "translation-missing"
        : target.status !== "published"
        ? "translation-unpublished"
        : null;
    if (reason) {
      return Object.freeze({
        kind: "unavailable",
        contentId: record.id,
        locale,
        englishPath,
        reason,
      });
    }
    return Object.freeze({
      kind: "available",
      contentId: record.id,
      locale,
      href: target!.publicPath,
    });
  }
  return Object.freeze({
    getRecords: () => Object.freeze([...byId.values()]),
    getContent: (id: string) => byId.get(id),
    getPublishedVersions: (id: string) => published.get(id) ?? EMPTY_VERSIONS,
    resolveCounterpart,
  });
}

// Kept as a compatibility export while consumers migrate from the pilot name.
export const PILOT_CONTENT_REGISTRY = createContentRegistry(
  [
    ...(contentInventory as readonly ContentRecord[]),
    ...ARTICLE_IDENTITIES.map((article) => ({
      id: article.id,
      versions: article.versions.map((version) => ({
        locale: version.locale,
        publicPath: version.publicPath,
        status: version.status,
        sourceRevision: version.translationSha256 ?? version.sourceSha256,
      })),
    })),
  ]
);
